import { doc, getDocFromServer, type CollectionReference } from 'firebase/firestore';
import type { RxReplicationWriteToMasterRow, WithDeleted } from 'rxdb';

// RxDB's Firestore replication plugin decides "conflict or not" on push with a plain read, which
// the Firestore SDK may answer from its cache while it still believes the client is offline — a
// stale cached doc then looks like the assumed master, no conflict is raised, and a stale local
// write overwrites newer server data. We can't patch the plugin, so its push handler is wrapped
// with one probe read that forces the SDK out of that state first. Online state is per client, so
// a single getDocFromServer on the first pushed id suffices (a non-existent id still proves the
// round trip), and any error propagates so RxDB retries instead of pushing against a stale read.
// That includes the `unavailable` rejection while offline: nothing is handed to the SDK's write
// queue until the server answers, and local-data-service keeps that expected error out of Sentry.
// Applied to every collection, not just tags: see docs/tag-counts/tag-counts-architecture.md.
export const withServerPreRead = <RxDocType>(
  handler: (rows: RxReplicationWriteToMasterRow<RxDocType>[]) => Promise<WithDeleted<RxDocType>[]>,
  collectionRef: CollectionReference,
  primaryPath: string
): typeof handler => {
  return async (rows) => {
    if (rows.length === 0) {
      return handler(rows);
    }

    const probeId = (rows[0].newDocumentState as Record<string, unknown>)[primaryPath] as string;
    await getDocFromServer(doc(collectionRef, probeId));

    return handler(rows);
  };
};
